// Custom hook for managing lenders with Firestore
import { useState, useEffect } from 'react';
import { Lender } from '../types';
import {
  getAllLenders,
  getLenderById,
  createLender,
  updateLender,
  deleteLender,
  subscribeLenders,
  searchLenders,
} from '../services/lenderService';

export const useLenders = (realTime: boolean = false) => {
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (realTime) {
      // Subscribe to real-time updates
      const unsubscribe = subscribeLenders(
        (updatedLenders) => {
          setLenders(updatedLenders);
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      // Fetch once
      fetchLenders();
    }
  }, [realTime]);

  const fetchLenders = async () => {
    try {
      setLoading(true);
      const data = await getAllLenders();
      setLenders(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addLender = async (lenderData: Omit<Lender, 'id'>) => {
    try {
      const id = await createLender(lenderData);
      if (!realTime) {
        await fetchLenders(); // Refresh list if not using real-time
      }
      return id;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const editLender = async (lenderId: string, updates: Partial<Omit<Lender, 'id'>>) => {
    try {
      await updateLender(lenderId, updates);
      if (!realTime) {
        await fetchLenders();
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const removeLender = async (lenderId: string) => {
    try {
      await deleteLender(lenderId);
      if (!realTime) {
        await fetchLenders();
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const search = async (searchTerm: string) => {
    try {
      setLoading(true);
      const results = await searchLenders(searchTerm);
      setLenders(results);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    fetchLenders();
  };

  return {
    lenders,
    loading,
    error,
    addLender,
    editLender,
    removeLender,
    search,
    refresh,
  };
};

export const useLender = (lenderId: string) => {
  const [lender, setLender] = useState<Lender | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLender();
  }, [lenderId]);

  const fetchLender = async () => {
    try {
      setLoading(true);
      const data = await getLenderById(lenderId);
      setLender(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    lender,
    loading,
    error,
    refresh: fetchLender,
  };
};
